import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { api } from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import {
  claimSourceConnectPrompt,
  forceClaimSourceConnectPrompt,
  isAnySourceConnectPromptOpen,
  onSourceConnectPromptClosed,
  releaseSourceConnectPrompt,
} from "./source-connect-prompt-guard";

const DISMISS_KEY = "as-microsoft-teams-dismiss";
const DISMISS_FOREVER_KEY = "as-microsoft-teams-dismiss-forever";

export function openMicrosoftTeamsConsent() {
  sessionStorage.removeItem(DISMISS_KEY);
  localStorage.removeItem(DISMISS_FOREVER_KEY);
  window.dispatchEvent(new CustomEvent("as-microsoft-teams-prompt"));
}

/**
 * Explains exactly what Microsoft's consent screen will ask for before
 * redirecting to Entra OAuth. Mirrors the Google calendar pre-consent:
 * auto-opens when no active Teams connection exists, and opens on demand
 * when the user clicks Connect on the Microsoft Teams card.
 */
export function MicrosoftTeamsConnectPrompt() {
  const { orgId, canManage, organization } = useOrg();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !canManage) return;

    async function maybeOpen(force = false) {
      if (
        !force &&
        (sessionStorage.getItem(DISMISS_KEY) === "1" ||
          localStorage.getItem(DISMISS_FOREVER_KEY) === "1")
      ) {
        return;
      }
      if (!force && isAnySourceConnectPromptOpen()) return;
      try {
        const teams = await api<{
          connections: Array<{ status: string }>;
          enabled?: boolean;
        }>(`/api/accounts/${orgId}/sources/microsoft_teams`).catch(() => ({
          connections: [] as Array<{ status: string }>,
          enabled: false,
        }));
        if (teams.enabled === false) return;
        const hasActive = teams.connections.some((c) => c.status === "active");
        if (hasActive) return;
        if (!force && !claimSourceConnectPrompt()) return;
        if (force) forceClaimSourceConnectPrompt();
        setError(null);
        setOpen(true);
      } catch {
        /* ignore */
      }
    }

    void maybeOpen(false);
    const onForce = () => void maybeOpen(true);
    const onClosed = () => void maybeOpen(false);
    window.addEventListener("as-microsoft-teams-prompt", onForce);
    const removeClosed = onSourceConnectPromptClosed(onClosed);
    return () => {
      window.removeEventListener("as-microsoft-teams-prompt", onForce);
      removeClosed();
    };
  }, [orgId, canManage]);

  function close(release = true) {
    setOpen(false);
    setBusy(false);
    if (release) releaseSourceConnectPrompt();
  }

  function skip() {
    sessionStorage.setItem(DISMISS_KEY, "1");
    close();
  }

  function dismissForever() {
    localStorage.setItem(DISMISS_FOREVER_KEY, "1");
    sessionStorage.removeItem(DISMISS_KEY);
    close();
  }

  async function continueToMicrosoft() {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/microsoft_teams/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organization?.name ?? "Microsoft Teams",
            returnPath: "/integrations",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Could not start Microsoft Teams connection",
      );
      setBusy(false);
    }
  }

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) skip();
        else setOpen(true);
      }}
      title="Connect Microsoft Teams"
      description="Microsoft will ask you to allow AttendeeSync to access your account. You can revoke access anytime in your Microsoft account settings."
      impact={
        <div className="space-y-2">
          <ul className="list-disc space-y-1 pl-4 text-sm text-rs-ink">
            <li>See your email address and basic profile (User.Read)</li>
            <li>
              View Teams webinars you organize or co-organize
              (VirtualEvent.Read)
            </li>
            <li>View webinar registrations for those webinars</li>
            <li>
              View your Outlook calendar appointments and their attendees
              (Calendars.Read)
            </li>
            <li>
              AttendeeSync will use this to discover webinars and create sync
              streams when Auto-Create is enabled
            </li>
          </ul>
          {error ? (
            <p role="alert" className="text-sm text-rs-danger">
              {error}
            </p>
          ) : null}
        </div>
      }
      contentClassName="w-[min(37rem,calc(100vw-2rem))]"
      cancelLabel="Skip"
      footerLeft={
        <Button
          type="button"
          variant="link"
          className="text-rs-danger"
          disabled={busy}
          onClick={dismissForever}
        >
          Dismiss Forever
        </Button>
      }
      confirmLabel={busy ? "Redirecting…" : "Continue To Microsoft"}
      busy={busy}
      onConfirm={() => void continueToMicrosoft()}
    />
  );
}
